'use client';

import React, { useEffect, useRef, useState } from 'react';
import { motion, useAnimationFrame } from 'motion/react';

import { sizes } from './levels';
import { useWindowSize } from 'usehooks-ts';
import { Point } from '@/lib/frontend/utils/oa-demo';
import { cn } from '@/lib/shared/ui';

export const pathString =
  'M960.386,374.887C1179.06,374.887 1356.59,552.42 1356.59,771.09C1356.59,989.76 1179.06,1167.29 960.386,1167.29C741.715,1167.29 564.183,989.76 564.183,771.09C564.183,552.42 741.715,374.887 960.386,374.887';

const viewBoxWidth = 2571;
const viewBoxHeight = 1549;

const offsetX = 0; // Offset to center the path in the container
const offsetY = 0;

export const PathItem = ({
  isMoving,
  container,
  children,
  className,
  onMove,
}: {
  isMoving: boolean;
  onMove?: (point: Point) => void;
  initial?: { x: number; y: number };
  container: React.RefObject<HTMLDivElement | null>;
  children?: React.ReactNode;
  className?: string;
}) => {
  const containerRef = useRef(null);

  const [offset, setOffset] = useState(25); // 0 to 100

  const pathRef = useRef<SVGPathElement>(null);

  useAnimationFrame(() => {
    if (isMoving) {
      const newOffset = offset < 100 ? offset + 0.01 : 0;
      setOffset(newOffset);

      const point = getPointOnPath(newOffset);

      onMove?.(point);
    }
  });

  useEffect(() => {
    // Create a hidden SVG path for calculations
    const svgNS = 'http://www.w3.org/2000/svg';
    const pathElem = document.createElementNS(svgNS, 'path');
    pathElem.setAttribute('d', pathString);
    pathRef.current = pathElem;
  }, []);

  const getPointOnPath = (percent: number) => {
    if (!pathRef.current) {
      return { x: 0, y: 0 };
    }
    const length = pathRef.current.getTotalLength();
    const point = pathRef.current.getPointAtLength((percent / 100) * length);
    return toScreenCoords({ x: point.x, y: point.y });
  };

  const toScreenCoords = (point: Point) => {
    const containerDimensions = container.current?.getBoundingClientRect() || {
      width: 0,
      height: 0,
    };

    const width = containerDimensions.width;
    const height = containerDimensions.height;

    return {
      x: (point.x / viewBoxWidth) * width + offsetX,
      y: (point.y / viewBoxHeight) * height + offsetY,
    };
  };

  const position = getPointOnPath(offset);

  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        height="100%"
        width="100%"
        viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        className="absolute inset-0 top-0 left-0 pointer-events-none"
        // style={{
        //   left: `${offsetX}px`,
        //   top: `${offsetY}px`,
        // }}
      >
        <path
          d={pathString}
          fill="transparent"
          strokeWidth="10"
          className="stroke-yellow-400/75"
          strokeLinecap="round"
          style={{
            left: `${offsetX}px`,
            top: `${offsetY}px`,
          }}
        />
      </svg>

      <div
        ref={containerRef}
        style={{
          transform: `translate(${position.x - sizes.object / 2}px, ${position.y - sizes.object / 2}px)`,
        }}
        className={cn('absolute inline z-10', className)}
      >
        {children}
      </div>
    </>
  );
};

export const MoveableItem = ({
  container,
  children,
  className,
  onDrag,
  onTapStart,
  initial = {
    x: 0,
    y: 0,
  },
}: {
  initial?: { x: number; y: number };
  container: React.RefObject<HTMLDivElement | null>;
  onDrag?: (point: Point) => void;
  onTapStart?: () => void;
  children?: React.ReactNode;
  className?: string;
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const size = useWindowSize();

  const getCenterPosition = () => {
    const containerRect = container.current?.getBoundingClientRect();
    const itemContainerRect = containerRef.current?.getBoundingClientRect();

    if (!containerRect || !itemContainerRect) {
      return { x: 0, y: 0 };
    }

    return {
      x: itemContainerRect.x - containerRect.x + itemContainerRect.width / 2,
      y: itemContainerRect.y - containerRect.y + itemContainerRect.height / 2,
    };
  };

  return (
    <motion.div
      drag
      key={JSON.stringify(size)} // Forcefully re-render when window size changes https://github.com/motiondivision/motion/issues/1659
      ref={containerRef}
      initial={initial}
      dragConstraints={container}
      dragMomentum={false}
      onDrag={() => {
        onDrag?.(getCenterPosition());
      }}
      onTapStart={() => {
        setIsDragging(true);
        onTapStart?.();
      }}
      onDragEnd={() => {
        setIsDragging(false);
      }}
      dragElastic={0.1}
      whileTap={{ scale: 1.2 }}
      className={cn(
        // 'top-0 left-0 w-10 h-10 bg-accent text-background-color rounded-full flex items-center justify-center',
        'absolute cursor-grab inline z-10',
        isDragging && 'cursor-grabbing',
        className,
      )}
    >
      {/* <div className="absolute w-1 h-1 bg-red-500 left-[22px] top-[22px]" /> */}
      {children}
    </motion.div>
  );
};
